import * as yup from "yup";

export interface IValidator {
  type: string;
  options?: object;
}

export interface IField {
  id: string;
  label: string;
  inputType: string;
  validators?: IValidator[];
}

// TODO: The `any` types here should be Yup chain objects.
type TFieldValidatorBuilder = (
  subject: any,
  rest: { field: IField; options?: object }
) => any;

// TODO: The `any` types here should be Yup chain objects.
type TFormValidatorBuilder = (
  subject: any,
  rest: { field: IField; options?: object }
) => any;

export interface IBuildSchemaArgs {
  fields?: IField[];
  formValidators?: IValidator[];
}

export class ValidatorRegistry {
  typeValidators: { [x: string]: TFieldValidatorBuilder };
  fieldValidators: { [x: string]: TFieldValidatorBuilder };
  formValidators: { [x: string]: TFormValidatorBuilder };

  constructor() {
    this.typeValidators = {};
    this.fieldValidators = {};
    this.formValidators = {};
  }

  /**
   * Register a validator builder to validate a field type.
   */
  registerFieldType(keys: string | string[], builder: TFieldValidatorBuilder) {
    if (!Array.isArray(keys)) {
      keys = [keys];
    }
    keys.forEach((k) => (this.typeValidators[k] = builder));
  }

  /**
   * Register a validator builder to validate a field.
   */
  registerFieldValidator(keys: string | string[], builder: TFieldValidatorBuilder) {
    if (!Array.isArray(keys)) {
      keys = [keys];
    }
    keys.forEach((k) => (this.fieldValidators[k] = builder));
  }

  /**
   * Register a validator builder to validate a form.
   */
  registerFormValidator(keys: string | string[], builder: TFormValidatorBuilder) {
    if (!Array.isArray(keys)) {
      keys = [keys];
    }
    keys.forEach((k) => (this.formValidators[k] = builder));
  }

  buildSchema(args: IBuildSchemaArgs) {
    const { fields, formValidators } = args;
    const shape = {};
    for (const field of fields) {
      let subject = this._startField(field);
      if (!subject) {
        continue;
      }
      subject = this._chainFieldValidators(field, subject);
      shape[field.id] = subject;
    }
    let subject = yup.object().shape(shape);
    subject = this._chainFormValidators(fields, formValidators, subject);
    return subject;
  }

  _startField(field: IField) {
    const builder = this.typeValidators[field.inputType];
    if (builder) {
      return builder(yup, { field });
    }
    return null;
  }

  _chainFieldValidators(field: IField, subject) {
    const validators = field?.validators || [];
    for (const validator of validators) {
      const { type, options } = validator;
      const builder = this.fieldValidators[type];
      if (builder) {
        subject = builder(subject, { options, field });
      }
    }
    return subject;
  }

  _chainFormValidators(fields: IField[], validators: IValidator[] = [], subject) {
    for (const validator of validators) {
      const { type, options } = validator;
      const builder = this.formValidators[type];
      if (builder) {
        subject = builder(subject, { options, fields });
      }
    }
    return subject;
  }
}

export const validatorRegistry = new ValidatorRegistry();
